import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { AllocationTable } from "../../../../../../Shared/IST/Components/AllocationTable";
import { initPensionInstrumentSelections } from "../../../../../../Shared/IST/Components/Shared/Utils";
import {
    distributionDividendPreferenceMap,
    distributionOptionsProcess,
    manageIncomeOptionsMap,
    ProcessType,
} from "../../../../../../Shared/IST/Resource/Content";
import { Urls } from "../../../../../Urls";
import { SubAccountTypeHelper } from "../../Helpers/SubAccountTypeHelper";

import { Typography } from "../../../../../../Shared/Typography/Typography";
import { DistributionDividendPreference } from "../../../../../../../Wrap/Constants/Enums/Domain/DistributionDividendPreference";
import { IReviewProps, prepareTypography } from "../ReviewParser";
import { NewQuoteAction } from "../../../NewQuoteWizard/Actions/NewQuoteAction";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { Action } from "../../../../../Actions/Action";

interface IInvestmentReview extends IReviewProps {
    ownerHierarchyId?: number;
    serviceProposition?: ServiceProposition;
}
export const InvestmentReview = (props: IInvestmentReview) => {
    const { data, colNum, extraInfo, process, subAccountType } = props;
    const [treasuredAssetProductIds, setTreasuredAssetProductIds] = React.useState<number[]>([]);

    React.useEffect(() => {
        if (props.ownerHierarchyId) {
            Action.getInstructionTreasuredAssetsProductIds(props.ownerHierarchyId, (data) => {
                setTreasuredAssetProductIds(data);
            });
        }
    }, [props.ownerHierarchyId]);

    const navigate = () => {
        if (process == ProcessType.NB) {
            Navigator.navigateToAndPreserveQueryParameters(investmentUrlConfig());
        } else if (process == ProcessType.Quote) {
            investmentUrlConfigForNewQuote();
        } else if (process == ProcessType.SinglePayment) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().SPTist);
        } else if (process == ProcessType.TopUpWithITFLS) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().TopUpWithITFLSIST);
        }else if (process == ProcessType.AddRegular || ProcessType.EditRegular) {
            Navigator.navigateToAndPreserveQueryParameters(Urls.get().RegularPaymentIst);
        }
    };

    const investmentUrlConfig = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return Urls.get().NBInvestmentSelectionPP;
            case SubAccountType.StocksIsa:
                return Urls.get().NBInvestmentSelectionISA;
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return Urls.get().NBInvestmentSelectionSIPP;
            default:
                break;
        }
    };
    const investmentUrlConfigForNewQuote = () => {
        switch (subAccountType) {
            case SubAccountType.InvestmentAccount:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteInvestmentSelectionPP);
            case SubAccountType.StocksIsa:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteInvestmentSelectionISA);
            case SubAccountType.Pension:
            case SubAccountType.Sipp:
            case SubAccountType.Npr:
                return NewQuoteAction.copyQuoteInstruction(Urls.get().QuoteInvestmentSelectionSIPP);
            default:
                break;
        }
    };

    let instrumentSelection = data?.InstrumentSelections?.Instruments;
    if (SubAccountTypeHelper.isOnPlatformPension(subAccountType)) {
        const preRetirement = data?.PreRetirementInstrumentSelections?.Instruments || [];
        const postRetirement = data?.PostRetirementInstrumentSelections?.Instruments || [];
        instrumentSelection = initPensionInstrumentSelections(preRetirement, postRetirement);
    }

    const bankAccountParse = (bankAccountId: number, bankAccountInstructions: any) => {
        const bankAccountList = bankAccountInstructions || [];
        if (bankAccountList.length == 0 || !bankAccountId) {
            return;
        }
        const bankAccount = bankAccountList.find((ele) => ele.Id == bankAccountId);
        return (
            <div>
                <span>
                    {bankAccount?.AccountNumber +
                        " " +
                        bankAccount?.BankCodes.BankCode +
                        "-" +
                        bankAccount?.BankCodes.BranchCode +
                        "-" +
                        bankAccount?.BankCodes.ExtraCode}
                </span>
            </div>
        );
    };

    const distributionOptionsConfig = () => {
        const config = [];
        if (
            data.DistributionDividendOptions &&
            data.DistributionDividendOptions.DistributionDividendPreference !=
                DistributionDividendPreference.NotSpecified
        ) {
            config.push({
                title: "Distribution options",
                data: (
                    <>
                        <span>
                            {SubAccountTypeHelper.isOnPlatformPension(subAccountType)
                                ? manageIncomeOptionsMap[
                                      data.DistributionDividendOptions.DistributionDividendPreference
                                  ]
                                : data.DistributionDividendOptions.DistributionDividendPreference ==
                                  DistributionDividendPreference.ProductCash
                                ? `Leave as cash in ${SubAccountTypeHelper.isIsa(subAccountType) ? "ISA" : "PP"}`
                                : distributionDividendPreferenceMap[
                                      data.DistributionDividendOptions.DistributionDividendPreference
                                  ]}
                        </span>
                        {data.DistributionDividendOptions.DistributionDividendPreference ==
                            DistributionDividendPreference.BankAccount && (
                            <>
                                <br />
                                <span>
                                    {bankAccountParse(
                                        data.DistributionDividendOptions.BankAccountId,
                                        extraInfo.BankAccountInstructions
                                    )}
                                </span>
                            </>
                        )}
                    </>
                ),
            });
        }
        return config;
    };

    return (
        <Accordion
            label="Investment selection"
            isOpen={false}
            onEdit={() => (props.onEditAction ? props.onEditAction() : navigate())}
        >
            <div className="pl-34">
                <AllocationTable
                    onRef={() => {}}
                    product={subAccountType}
                    type={process}
                    modelData={data}
                    data={instrumentSelection}
                    onChange={() => {}}
                    onToggle={() => {}}
                    isAssetClass={false}
                    readonly={true}
                    cssWrapper="no-sided-padding no-borders bg-default"
                    treasuredAssetsProductIds={treasuredAssetProductIds || []}
                    accountServiceProposition={props.serviceProposition}
                />
                {distributionOptionsProcess.includes(process) && (
                    <Typography {...prepareTypography(distributionOptionsConfig(), colNum, "auto-width")}></Typography>
                )}
            </div>
        </Accordion>
    );
};
